import { calculatePromoUnitPrice, formatTotemPrice } from '@/features/totems/domain/service/totem-price';

/** De dónde sale el cupón: vinculado al producto o a una de sus categorías. */
export type MenuDiscountSource = 'product' | 'category';

/** Cupón vigente que aplica a un producto (informativo: el cobro sigue siendo por código en el carrito). */
export type MenuDiscountCandidate = {
  id?: string;
  code: string;
  /** Porcentaje (0-100) o monto fijo, según `type`. */
  amount: number;
  type: 'percentage' | 'fixed';
  method: 'discount' | 'promo';
  source: MenuDiscountSource;
  minPurchase: number | null;
};

export type MenuDiscount = MenuDiscountCandidate & {
  /** Importe que se ahorra con el cupón, truncado a 2 decimales. */
  discountAmount: number;
  /** Precio del producto con el cupón aplicado. */
  finalPrice: number;
};

/** Campos de vigencia de `discounts_codes` necesarios para saber si el cupón se puede comunicar. */
export type MenuDiscountWindow = {
  start_at: string | null;
  expire_at: string | null;
  days: string[] | null;
  disabled_at: string | null;
  deleted_at: string | null;
  usages: number | null;
  usages_count: number | null;
};

/**
 * Misma vigencia que `validateDiscountCode` (estado, ventana de fechas, días y usos).
 * `min_purchase` **no** se evalúa acá: el badge informa, no cobra; el panel del carrito
 * valida el mínimo al aplicar el código.
 */
export function isMenuDiscountActive(discount: MenuDiscountWindow, now = new Date()): boolean {
  if (discount.deleted_at || discount.disabled_at) return false;
  if (discount.start_at && new Date(discount.start_at) > now) return false;
  if (discount.expire_at && new Date(discount.expire_at) < now) return false;
  if (discount.usages != null && (discount.usages_count ?? 0) >= discount.usages) return false;

  if (discount.days && discount.days.length > 0) {
    const today = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    if (!discount.days.includes(today)) return false;
  }

  return true;
}

function applyToPrice(
  price: number,
  candidate: MenuDiscountCandidate
): Pick<MenuDiscount, 'discountAmount' | 'finalPrice'> {
  // Misma regla de centavos que el servidor (descuento truncado antes de restar).
  const { discountAmount, finalPrice } = calculatePromoUnitPrice(price, {
    discount: candidate.amount,
    discountType: candidate.type,
  });
  return { finalPrice, discountAmount };
}

/**
 * Elige el cupón que más ahorra para ese precio. En empate gana el vinculado al producto
 * (es más específico que el de categoría). Devuelve `null` si ninguno descuenta algo.
 */
export function resolveMenuDiscount(
  price: number,
  candidates: MenuDiscountCandidate[] | undefined
): MenuDiscount | null {
  if (!Number.isFinite(price) || price <= 0 || !candidates?.length) return null;

  let best: MenuDiscount | null = null;

  for (const candidate of candidates) {
    const applied = applyToPrice(price, candidate);
    if (applied.discountAmount <= 0) continue;

    const isBetter =
      !best ||
      applied.discountAmount > best.discountAmount ||
      (applied.discountAmount === best.discountAmount && candidate.source === 'product' && best.source !== 'product');

    if (isBetter) best = { ...candidate, ...applied };
  }

  return best;
}

/** Etiqueta corta para el badge: `10% OFF` / `$500 OFF`. */
export function formatMenuDiscountLabel(discount: Pick<MenuDiscount, 'amount' | 'type'>): string {
  return discount.type === 'percentage' ? `${discount.amount}% OFF` : `${formatTotemPrice(discount.amount)} OFF`;
}

/**
 * Mejor promo auto-aplicable de la línea (solo candidatos `method === 'promo'`).
 * Los cupones (`method === 'discount'`) NO se auto-aplican: se cobran tipeando el código.
 * Devuelve `null` si no hay promo vigente que descuente algo.
 */
export function resolveLinePromo(price: number, candidates: MenuDiscountCandidate[] | undefined): MenuDiscount | null {
  return resolveMenuDiscount(
    price,
    (candidates ?? []).filter(candidate => candidate.method === 'promo')
  );
}

/** Cupones disponibles para los ítems del carrito (el de mayor ahorro por código), para el hint del panel. */
export function getAvailableCartDiscounts(
  items: { productId: string; unitPrice: number }[],
  discountsByProduct: Record<string, MenuDiscountCandidate[]>
): MenuDiscount[] {
  const byCode = new Map<string, MenuDiscount>();

  for (const item of items) {
    // Solo cupones de carrito: las promos (method 'promo') se aplican solas por línea.
    const candidates = (discountsByProduct[item.productId] ?? []).filter(candidate => candidate.method === 'discount');
    const discount = resolveMenuDiscount(item.unitPrice, candidates);
    if (!discount || !discount.code.trim()) continue;

    const existing = byCode.get(discount.code);
    if (!existing || discount.discountAmount > existing.discountAmount) {
      byCode.set(discount.code, discount);
    }
  }

  return [...byCode.values()];
}
